import config from '../config.js'
import { useAuthStore } from '../stores/authStore.js'

// Global API utility with safe error handling
class ApiClient {
  constructor() {
    this.baseUrl = config.apiBaseUrl
    this.authStore = null
  }

  // Initialize with auth store reference
  setAuthStore(authStore) {
    this.authStore = authStore
  }

  // Get auth headers safely
  getAuthHeaders() {
    if (!this.authStore) return {}
    
    const token = this.authStore.token
    if (!token) return {}
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  // Safe request method with no retries
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`
    
    // Merge headers
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      })

      // Handle authentication errors immediately
      if (response.status === 401) {
        if (this.authStore) {
          await this.authStore.logout()
        }
        throw new Error('Authentication failed - please log in again')
      }

      // Handle other errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Request failed: ${response.status}`)
      }

      return response

    } catch (error) {
      // Network errors - no retries, just fail
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error - please check your connection')
      }
      
      // Re-throw other errors
      throw error
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    const response = await this.request(endpoint, {
      method: 'GET',
      ...options
    })
    return response.json()
  }

  // POST request
  async post(endpoint, data, options = {}) {
    const response = await this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    })
    return response.json()
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    const response = await this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options
    })
    return response.json()
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    const response = await this.request(endpoint, {
      method: 'DELETE',
      ...options
    })
    return response.json()
  }

  // File upload (for CSV exports)
  async download(endpoint, options = {}) {
    const response = await this.request(endpoint, {
      method: 'GET',
      ...options
    })
    return response.blob()
  }
}

// Create singleton instance
const apiClient = new ApiClient()

export default apiClient
